import { FC, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Drawer,
  Button,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Edit,
  Delete,
  Add,
  Download,
  Save,
  Cancel,
} from "@mui/icons-material";
import {
  useAddDocumentIdentifierMutation,
  useUpdateDocumentIdentifierMutation,
  useRemoveDocumentIdentifierMutation,
  useLazyGetCustomsDocumentQuery,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { getDocumentTypeFullLabel, getStatusLabel } from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

const IDENTIFIER_TYPES: { value: CustomsIdentifierType; label: string }[] = [
  { value: "ENTRY", label: "Entry #" },
  { value: "PO", label: "PO" },
  { value: "INV", label: "Invoice" },
  { value: "BL", label: "B/L" },
  { value: "CONTAINER", label: "Container" },
  { value: "OTHER", label: "Other" },
];

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  ENTRY: { bg: "#E0F2F1", color: "#00695C" },
  PO: { bg: "#E3F2FD", color: "#1565C0" },
  INV: { bg: "#FFF8E1", color: "#F57F17" },
  BL: { bg: "#F3E5F5", color: "#7B1FA2" },
  CONTAINER: { bg: "#F5F5F5", color: "#616161" },
  OTHER: { bg: "#ECEFF1", color: "#546E7A" },
};

const SOURCE_LABELS: Record<string, string> = {
  REGEX: "Regex",
  LLM: "AI",
  PROPAGATED: "Propagated",
  MANUAL: "Manual",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export interface DocumentDetailDialogProps {
  open: boolean;
  onClose: () => void;
  document: CustomsDocument | null;
  tenantId?: string;
}

export const DocumentDetailDialog: FC<DocumentDetailDialogProps> = ({
  open,
  onClose,
  document: doc,
  tenantId,
}) => {
  const { show } = useSnackbar();

  // Mutations
  const [addIdentifier, { isLoading: isAdding }] = useAddDocumentIdentifierMutation();
  const [updateIdentifier, { isLoading: isUpdating }] = useUpdateDocumentIdentifierMutation();
  const [removeIdentifier, { isLoading: isRemoving }] = useRemoveDocumentIdentifierMutation();
  const [getDocument] = useLazyGetCustomsDocumentQuery();

  // Local document state — updated immediately from mutation responses
  const [localDoc, setLocalDoc] = useState<CustomsDocument | null>(doc);

  // Sync local state when prop changes (e.g., parent re-fetches or different doc selected)
  useEffect(() => {
    setLocalDoc(doc);
  }, [doc]);

  // Form state
  const [addMode, setAddMode] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [addType, setAddType] = useState<CustomsIdentifierType>("ENTRY");
  const [addValue, setAddValue] = useState("");
  const [editType, setEditType] = useState<CustomsIdentifierType>("ENTRY");
  const [editValue, setEditValue] = useState("");

  // Reset state when dialog opens/closes or document changes
  useEffect(() => {
    if (!open) {
      setAddMode(false);
      setEditIndex(null);
      setAddType("ENTRY");
      setAddValue("");
      setEditType("ENTRY");
      setEditValue("");
    }
  }, [open, doc?._id]);

  // Use localDoc for rendering (falls back to prop)
  const displayDoc = localDoc || doc;
  if (!displayDoc) return null;

  const identifiers = displayDoc.identifiers || [];

  const handleAdd = async () => {
    if (!addValue.trim()) return;
    try {
      const updated = await addIdentifier({
        docId: displayDoc._id,
        type: addType,
        value: addValue.trim(),
        tenantId,
      }).unwrap();
      setLocalDoc(updated);
      show("Identifier Added", "Identifier added and document re-clustered.", "success");
      setAddMode(false);
      setAddType("ENTRY");
      setAddValue("");
    } catch (error) {
      show("Error", (error as Error).message || "Failed to add identifier", "error");
    }
  };

  const handleUpdate = async () => {
    if (editIndex === null || !editValue.trim()) return;
    try {
      const updated = await updateIdentifier({
        docId: displayDoc._id,
        index: editIndex,
        type: editType,
        value: editValue.trim(),
        tenantId,
      }).unwrap();
      setLocalDoc(updated);
      show("Identifier Updated", "Identifier updated and document re-clustered.", "success");
      setEditIndex(null);
    } catch (error) {
      show("Error", (error as Error).message || "Failed to update identifier", "error");
    }
  };

  const handleRemove = async (index: number) => {
    try {
      const updated = await removeIdentifier({
        docId: displayDoc._id,
        index,
        tenantId,
      }).unwrap();
      setLocalDoc(updated);
      show("Identifier Removed", "Identifier removed and document re-clustered.", "success");
    } catch (error) {
      show("Error", (error as Error).message || "Failed to remove identifier", "error");
    }
  };

  const startEdit = (index: number) => {
    const id = identifiers[index];
    setEditIndex(index);
    setEditType(id.type);
    setEditValue(id.value);
  };

  const handleDownload = async () => {
    try {
      const result = await getDocument({
        docId: displayDoc._id,
        tenantId,
      }).unwrap();
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
      }
    } catch (error) {
      show("Download Failed", (error as Error).message, "error");
    }
  };

  const isMutating = isAdding || isUpdating || isRemoving;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: 440, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 2,
            borderBottom: `1px solid ${tokens.border.default}`,
            pr: 6,
            position: "relative",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={displayDoc.filename} arrow>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: tokens.fontWeight.bold,
                  fontSize: tokens.fontSize.xl,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayDoc.filename}
              </Typography>
            </Tooltip>
            <Stack direction="row" gap={0.75} mt={0.5}>
              {displayDoc.documentType && (
                <Chip
                  label={getDocumentTypeFullLabel(displayDoc.documentType)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: tokens.fontSize["2xs"],
                    fontWeight: tokens.fontWeight.semibold,
                    bgcolor: tokens.bg.muted,
                    color: tokens.text.body,
                  }}
                />
              )}
              <Chip
                label={getStatusLabel(displayDoc.status)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: tokens.fontSize["2xs"],
                  fontWeight: tokens.fontWeight.bold,
                  bgcolor:
                    displayDoc.status === "READY"
                      ? tokens.chip.green.bg
                      : displayDoc.status === "FAILED"
                        ? tokens.status.dangerBg
                        : tokens.status.warningBg,
                  color:
                    displayDoc.status === "READY"
                      ? tokens.chip.green.text
                      : displayDoc.status === "FAILED"
                        ? "#991B1B"
                        : tokens.chip.amber.text,
                }}
              />
            </Stack>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {/* Metadata Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"] }}>
                  File Size
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
                  {formatBytes(displayDoc.sizeBytes)}
                </Typography>
              </Box>
              {displayDoc.pageCount != null && (
                <Box>
                  <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"] }}>
                    Pages
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
                    {displayDoc.pageCount}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"] }}>
                  Uploaded
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
                  {formatDate(displayDoc.createdAt)}
                </Typography>
              </Box>
              {displayDoc.processedAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"] }}>
                    Processed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
                    {formatDate(displayDoc.processedAt)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Identifiers Section */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.lg }}>
                Identifiers ({identifiers.length})
              </Typography>
              {!addMode && (
                <Button
                  size="small"
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  onClick={() => setAddMode(true)}
                  disabled={isMutating}
                  sx={{
                    fontSize: tokens.fontSize.base,
                    color: tokens.brand.primary,
                  }}
                >
                  Add
                </Button>
              )}
            </Stack>

            {/* Add Form */}
            {addMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                  p: 1.5,
                  bgcolor: tokens.status.successBg,
                  borderRadius: tokens.radius.md,
                  border: "1px solid #99F6E4",
                  alignItems: "center",
                }}
              >
                <Select
                  size="small"
                  value={addType}
                  onChange={(e) => setAddType(e.target.value as CustomsIdentifierType)}
                  sx={{ minWidth: 110, fontSize: tokens.fontSize.base, height: 36 }}
                >
                  {IDENTIFIER_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value} sx={{ fontSize: tokens.fontSize.base }}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  size="small"
                  placeholder="Enter value..."
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                    if (e.key === "Escape") setAddMode(false);
                  }}
                  autoFocus
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-input": { fontSize: tokens.fontSize.base, py: "8px" },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleAdd}
                  disabled={!addValue.trim() || isAdding}
                  sx={{ color: tokens.brand.primary }}
                >
                  {isAdding ? <CircularProgress size={16} /> : <Save sx={{ fontSize: 18 }} />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setAddMode(false)}
                  disabled={isAdding}
                >
                  <Cancel sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}

            {/* Identifier List */}
            {identifiers.length === 0 && !addMode ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "#FAFBFC",
                  borderRadius: tokens.radius.md,
                  border: `1px dashed ${tokens.border.default}`,
                }}
              >
                <Typography variant="body2" sx={{ color: tokens.text.muted, mb: 1 }}>
                  No identifiers found
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  onClick={() => setAddMode(true)}
                  sx={{
                    fontSize: tokens.fontSize.base,
                    color: tokens.brand.primary,
                  }}
                >
                  Add Identifier
                </Button>
              </Box>
            ) : (
              <Stack gap={0.75}>
                {identifiers.map((id, index) => {
                  const colors = TYPE_COLORS[id.type] || TYPE_COLORS.OTHER;
                  const isEditing = editIndex === index;

                  if (isEditing) {
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 1,
                          p: 1.5,
                          bgcolor: "#FFFBEB",
                          borderRadius: tokens.radius.md,
                          border: "1px solid #FDE68A",
                          alignItems: "center",
                        }}
                      >
                        <Select
                          size="small"
                          value={editType}
                          onChange={(e) => setEditType(e.target.value as CustomsIdentifierType)}
                          sx={{ minWidth: 110, fontSize: tokens.fontSize.base, height: 36 }}
                        >
                          {IDENTIFIER_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value} sx={{ fontSize: tokens.fontSize.base }}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleUpdate();
                            if (e.key === "Escape") setEditIndex(null);
                          }}
                          autoFocus
                          sx={{
                            flex: 1,
                            "& .MuiInputBase-input": { fontSize: tokens.fontSize.base, py: "8px" },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={handleUpdate}
                          disabled={!editValue.trim() || isUpdating}
                          sx={{ color: tokens.brand.primary }}
                        >
                          {isUpdating ? <CircularProgress size={16} /> : <Save sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setEditIndex(null)}
                          disabled={isUpdating}
                        >
                          <Cancel sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        px: 1.5,
                        bgcolor: "#FAFBFC",
                        borderRadius: tokens.radius.sm,
                        border: `1px solid ${tokens.border.light}`,
                        "&:hover": { bgcolor: tokens.bg.muted },
                        "&:hover .identifier-actions": { opacity: 1 },
                      }}
                    >
                      <Chip
                        label={
                          IDENTIFIER_TYPES.find((t) => t.value === id.type)?.label || id.type
                        }
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: tokens.fontSize["2xs"],
                          fontWeight: tokens.fontWeight.bold,
                          bgcolor: colors.bg,
                          color: colors.color,
                          minWidth: 65,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontFamily: "monospace",
                          fontSize: tokens.fontSize.base,
                          fontWeight: tokens.fontWeight.semibold,
                        }}
                      >
                        {id.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"], minWidth: 55 }}
                      >
                        {SOURCE_LABELS[id.extractedBy] || id.extractedBy}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.text.muted, fontSize: tokens.fontSize["2xs"], minWidth: 35 }}
                      >
                        {Math.round(id.confidence * 100)}%
                      </Typography>
                      <Stack
                        direction="row"
                        gap={0.25}
                        className="identifier-actions"
                        sx={{ opacity: 0, transition: "opacity 0.15s" }}
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => startEdit(index)}
                            disabled={isMutating}
                            sx={{ p: 0.25 }}
                          >
                            <Edit sx={{ fontSize: 15, color: tokens.text.secondary }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleRemove(index)}
                            disabled={isMutating}
                            sx={{ p: 0.25 }}
                          >
                            {isRemoving ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Delete sx={{ fontSize: 15, color: tokens.status.danger }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: `1px solid ${tokens.border.default}`, display: "flex", gap: 1 }}>
          <Button
            size="small"
            startIcon={<Download sx={{ fontSize: 16 }} />}
            onClick={handleDownload}
            variant="outlined"
            sx={{
              borderColor: tokens.brand.primary,
              color: tokens.brand.primary,
            }}
          >
            Download
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
